import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { BackendApiService } from './backend-api.service';
import { ProjectSummary } from '../models/api-dto.model';

export const STORAGE_PROJECT_KEY = 'sprinta_active_project_id';

@Injectable({
  providedIn: 'root'
})
export class ProjectContextService {

  private currentProjectSubject = new BehaviorSubject<ProjectSummary | null>(null);
  public currentProject$: Observable<ProjectSummary | null> = this.currentProjectSubject.asObservable();

  private userProjectsSubject = new BehaviorSubject<ProjectSummary[]>([]);
  public userProjects$: Observable<ProjectSummary[]> = this.userProjectsSubject.asObservable();

  private initObservable: Observable<ProjectSummary> | null = null;
  private loadProjectsObservable: Observable<ProjectSummary[]> | null = null;

  constructor(
    private backendApi: BackendApiService,
    private router: Router
  ) {
    // Attempt background initialization
    this.ensureProject().subscribe({
      error: (err) => console.warn('ProjectContextService: initial project resolution pending auth', err)
    });
  }

  get currentProject(): ProjectSummary | null {
    return this.currentProjectSubject.value;
  }

  get userProjects(): ProjectSummary[] {
    return this.userProjectsSubject.value;
  }

  getActiveProjectId(): string {
    return this.currentProjectSubject.value?.id || localStorage.getItem(STORAGE_PROJECT_KEY) || '';
  }

  setCurrentProject(project: ProjectSummary): void {
    if (project && project.id) {
      localStorage.setItem(STORAGE_PROJECT_KEY, project.id);
      this.currentProjectSubject.next(project);

      // Keep userProjects list updated if needed
      const projects = this.userProjectsSubject.value;
      const idx = projects.findIndex(p => p.id === project.id);
      if (idx >= 0) {
        projects[idx] = { ...projects[idx], ...project };
        this.userProjectsSubject.next([...projects]);
      } else if (projects.length > 0) {
        this.userProjectsSubject.next([...projects, project]);
      }
    }
  }

  /**
   * Updates a project's name, synchronizing local state and the active project subject.
   */
  updateProjectName(projectId: string, name: string): Observable<ProjectSummary> {
    const trimmed = name.trim();
    return this.backendApi.updateProject(projectId, { name: trimmed }).pipe(
      tap((updated) => {
        const newName = updated.name || trimmed;
        const list = this.userProjectsSubject.value.map(p =>
          p.id === projectId ? { ...p, name: newName } : p
        );
        this.userProjectsSubject.next(list);

        const current = this.currentProjectSubject.value;
        if (current && current.id === projectId) {
          this.currentProjectSubject.next({
            ...current,
            name: newName
          });
        }
      })
    );
  }

  /**
   * Permanently deletes an empty project, synchronizing userProjects and active project.
   */
  deleteProject(projectId: string): Observable<{ deleted: boolean; projectId: string }> {
    return this.backendApi.deleteProject(projectId).pipe(
      tap(() => {
        const remaining = this.userProjectsSubject.value.filter(p => p.id !== projectId);
        this.userProjectsSubject.next(remaining);

        const current = this.currentProjectSubject.value;
        if (current && current.id === projectId) {
          if (remaining.length > 0) {
            this.setCurrentProject(remaining[0]);
          } else {
            this.currentProjectSubject.next(null as any);
            localStorage.removeItem(STORAGE_PROJECT_KEY);
          }
        }
      })
    );
  }

  loadUserProjects(forceRefresh = false): Observable<ProjectSummary[]> {
    if (forceRefresh) {
      this.loadProjectsObservable = null;
    } else if (this.userProjectsSubject.value.length > 0) {
      return of(this.userProjectsSubject.value);
    }

    if (this.loadProjectsObservable) {
      return this.loadProjectsObservable;
    }

    this.loadProjectsObservable = this.backendApi.getCurrentUser().pipe(
      map(res => {
        const projects = res.projects || [];
        this.userProjectsSubject.next(projects);
        return projects;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
      finalize(() => {
        this.loadProjectsObservable = null;
      }),
      catchError(err => {
        this.loadProjectsObservable = null;
        console.warn('ProjectContextService: error loading user projects', err);
        return of(this.userProjectsSubject.value);
      })
    );

    return this.loadProjectsObservable;
  }

  /**
   * Switches project and navigates to the scoped route in the new project.
   */
  switchProject(projectId: string, targetSuffix: string = 't/roadmap'): void {
    if (!projectId) return;

    const existing = this.userProjectsSubject.value.find(p => p.id === projectId);
    if (existing) {
      this.setCurrentProject(existing);
      this.router.navigateByUrl(`/p/${projectId}/${targetSuffix}`);
      return;
    }

    this.backendApi.getProject(projectId).pipe(
      tap(dto => {
        const summary: ProjectSummary = {
          id: dto.id,
          name: dto.name,
          role: dto.role,
          status: dto.status,
          ownerUserId: dto.ownerUserId,
          createdAt: dto.createdAt,
          updatedAt: dto.updatedAt,
          version: dto.version
        };
        this.setCurrentProject(summary);
        this.router.navigateByUrl(`/p/${projectId}/${targetSuffix}`);
      }),
      catchError(err => {
        console.error('ProjectContextService: failed to switch project', err);
        return of(null);
      })
    ).subscribe();
  }

  /**
   * Sets project context by ID when navigated directly via route URL /p/:projectId/...
   */
  setActiveProjectId(projectId: string): Observable<ProjectSummary | null> {
    if (!projectId) return of(null);

    const current = this.currentProjectSubject.value;
    if (current && current.id === projectId) {
      return of(current);
    }

    const cached = this.userProjectsSubject.value.find(p => p.id === projectId);
    if (cached) {
      this.setCurrentProject(cached);
      return of(cached);
    }

    return this.loadUserProjects(true).pipe(
      switchMap(projects => {
        const found = projects.find(p => p.id === projectId);
        if (found) {
          this.setCurrentProject(found);
          return of(found);
        }

        return this.backendApi.getProject(projectId).pipe(
          map(dto => {
            const summary: ProjectSummary = {
              id: dto.id,
              name: dto.name,
              role: dto.role,
              status: dto.status,
              ownerUserId: dto.ownerUserId,
              createdAt: dto.createdAt,
              updatedAt: dto.updatedAt,
              version: dto.version
            };
            this.setCurrentProject(summary);
            return summary;
          }),
          catchError(() => of(null))
        );
      })
    );
  }

  ensureProject(): Observable<ProjectSummary> {
    const current = this.currentProjectSubject.value;
    if (current && current.id) {
      return of(current);
    }

    if (this.initObservable) {
      return this.initObservable;
    }

    this.initObservable = this.backendApi.getCurrentUser().pipe(
      switchMap(res => {
        const projects = res.projects || [];
        this.userProjectsSubject.next(projects);

        if (projects.length > 0) {
          const cachedId = localStorage.getItem(STORAGE_PROJECT_KEY);
          const saved = cachedId ? projects.find(p => p.id === cachedId) : null;
          const active = saved
            || projects.find(p => p.role === 'owner' && p.status === 'active')
            || projects.find(p => p.status === 'active')
            || projects[0];

          this.setCurrentProject(active);
          return of(active);
        }

        const defaultName = res.user?.displayName
          ? `${res.user.displayName}'s Project`
          : 'My Sprinta Project';

        return this.backendApi.createProject({
          name: defaultName,
          description: 'Primary Sprinta Project'
        }).pipe(
          tap(newProject => {
            this.userProjectsSubject.next([newProject]);
            this.setCurrentProject(newProject);
          })
        );
      }),
      tap({
        complete: () => {
          this.initObservable = null;
        }
      }),
      catchError(err => {
        this.initObservable = null;
        const cachedId = localStorage.getItem(STORAGE_PROJECT_KEY);
        if (cachedId) {
          const fallback: ProjectSummary = {
            id: cachedId,
            name: 'Sprinta Project',
            role: 'owner',
            status: 'active',
            ownerUserId: '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            version: 1
          };
          this.currentProjectSubject.next(fallback);
          return of(fallback);
        }
        throw err;
      })
    );

    return this.initObservable;
  }

  ensureProjectId(): Observable<string> {
    const activeId = this.getActiveProjectId();
    if (activeId) {
      return of(activeId);
    }
    return this.ensureProject().pipe(map(p => p.id));
  }
}
